import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  ConfirmationNumber,
  CreditCard,
  Remove,
  Warning,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { GET_STORE_BY_ID } from '../graphql/queries';
import { CREATE_SEAT_BILLING_SETUP_INTENT, UPDATE_STORE_SEAT_COUNT } from '../graphql/mutations';
import { useConfirmation } from '../hooks/useConfirmation';
import { CustomSnackbar } from '../components/common/CustomSnackbar';
import LoadingSpinner from '../components/common/LoadingSpinner';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Currently hardcoded — must match backend FREE_SEAT_COUNT constant in
// barber/graphql/payment.py. If we ever make it dynamic per-store, expose
// it on StoreType so the frontend can read it from the same source.
const FREE_SEATS = 2;
// Per-seat monthly cost in pence — kept in pence to dodge floating-point
// rounding in the cost preview. £5 = 500 pence.
const SEAT_PRICE_PENCE = 500;
// Hard upper bound to keep the +/- input reasonable. Server has no cap.
const MAX_SEATS = 50;

function formatGBP(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

// ─── Card setup sub-form (must live inside <Elements>) ──────────────────────

interface CardSetupFormProps {
  clientSecret: string;
  onSuccess: (paymentMethodId: string) => void;
  onCancel: () => void;
  saving: boolean;
}

const CardSetupForm = ({ clientSecret, onSuccess, onCancel, saving }: CardSetupFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setConfirming(true);
    setCardError(null);

    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: cardElement },
    });

    setConfirming(false);

    if (error) {
      setCardError(error.message ?? 'Card setup failed. Please try again.');
    } else if (setupIntent?.payment_method) {
      onSuccess(setupIntent.payment_method as string);
    }
  };

  const busy = confirming || saving;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        Add a card for seat billing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your card will be charged monthly for any paid seats. You can update it later.
      </Typography>

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          mb: 2,
          bgcolor: 'background.paper',
        }}
      >
        <CardElement
          options={{
            style: {
              base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
              invalid: { color: '#9e2146' },
            },
          }}
        />
      </Box>

      {cardError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cardError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="text" onClick={onCancel} disabled={busy} sx={{ fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={busy || !stripe}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CreditCard />}
          sx={{ borderRadius: 2, px: 3, py: 1.25, fontWeight: 600 }}
        >
          {busy ? 'Processing…' : 'Save card & confirm seats'}
        </Button>
      </Stack>
    </Box>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

const ManageSeats = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId } = useAuth();
  const storeId = paramStoreId || authStoreId;
  const [searchParams] = useSearchParams();

  const {
    snackbarState,
    closeSnackbar,
    showSuccessSnackbar,
    showErrorSnackbar,
  } = useConfirmation();

  const { data, loading, refetch } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId },
    skip: !storeId,
    fetchPolicy: 'cache-and-network',
  });

  const store = data?.storeDetails;
  const seatCount: number = store?.seatCount ?? FREE_SEATS;
  const activeSeatsUsed: number = store?.activeSeatsUsed ?? 0;
  const chargesEnabled: boolean = store?.chargesEnabled ?? false;
  const hasSeatSubscription: boolean = Boolean(store?.seatLicenseSubscriptionId);

  const [draft, setDraft] = useState<number>(seatCount);
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus === 'add-1') {
      setDraft(Math.min(MAX_SEATS, seatCount + 1));
    } else {
      setDraft(seatCount);
    }
  }, [seatCount, searchParams]);

  // Card setup state — set when the user is about to buy their first paid seat.
  const [cardSetup, setCardSetup] = useState<{ clientSecret: string } | null>(null);
  // Confirmation dialog — shown when an existing subscription is being modified.
  const [showConfirm, setShowConfirm] = useState(false);
  // Ref holds the target seat count across async boundaries (refs are synchronous,
  // unlike state which only applies on the next render).
  const pendingCountRef = useRef<number>(draft);

  const [updateSeats, { loading: saving }] = useMutation(UPDATE_STORE_SEAT_COUNT);
  const [createSetupIntent, { loading: creatingIntent }] = useMutation(CREATE_SEAT_BILLING_SETUP_INTENT);

  const minSeats = Math.max(FREE_SEATS, activeSeatsUsed);
  const dec = () => setDraft((d) => Math.max(minSeats, d - 1));
  const inc = () => setDraft((d) => Math.min(MAX_SEATS, d + 1));

  const { paidNow, paidDraft, monthlyDiffPence } = useMemo(() => {
    const paidNow = Math.max(0, seatCount - FREE_SEATS);
    const paidDraft = Math.max(0, draft - FREE_SEATS);
    const monthlyDiffPence = (paidDraft - paidNow) * SEAT_PRICE_PENCE;
    return { paidNow, paidDraft, monthlyDiffPence };
  }, [seatCount, draft]);

  const hasChange = draft !== seatCount;
  // Card required when buying into paid tier for the first time (no existing subscription).
  const needsCard = draft > FREE_SEATS && !hasSeatSubscription;

  const doUpdateSeats = async (paymentMethodId?: string) => {
    try {
      const result = await updateSeats({
        variables: { storeId, newSeatCount: pendingCountRef.current, paymentMethodId },
      });
      if (result.data?.updateStoreSeatCount?.ok) {
        showSuccessSnackbar('Seats updated.');
        setShowConfirm(false);
        await refetch();
      } else {
        const errors = result.data?.updateStoreSeatCount?.errors || ['Unknown error'];
        showErrorSnackbar(errors.join(', '));
      }
    } catch {
      showErrorSnackbar('Could not update seats. Please try again.');
    }
  };

  const handleSave = async () => {
    pendingCountRef.current = draft; // always synchronous — no async stale-state risk

    if (needsCard) {
      // First paid purchase — collect card via SetupIntent.
      try {
        const result = await createSetupIntent({ variables: { storeId } });
        const payload = result.data?.createSeatBillingSetupIntent;
        if (!payload?.ok || !payload?.clientSecret) {
          showErrorSnackbar(payload?.errors?.[0] ?? 'Could not initialise payment setup.');
          return;
        }
        setCardSetup({ clientSecret: payload.clientSecret });
      } catch {
        showErrorSnackbar('Could not initialise payment setup. Please try again.');
      }
      return;
    }

    if (hasSeatSubscription) {
      // Existing subscription — confirm before touching Stripe.
      setShowConfirm(true);
      return;
    }

    await doUpdateSeats();
  };

  const handleCardSuccess = async (paymentMethodId: string) => {
    setCardSetup(null);
    await doUpdateSeats(paymentMethodId);
  };

  if (loading && !store) {
    return <LoadingSpinner message="Loading seats…" />;
  }

  if (!storeId || !store) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Store not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: '720px',
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <IconButton
            component={Link}
            to={`/store/${storeId}/staff`}
            size="small"
            aria-label="Back to staff"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="overline" color="text.secondary">
            Staff
          </Typography>
        </Stack>
        <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 1 }}>
          Manage seats
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Each seat lets you have one active barber. The first {FREE_SEATS} are free —
          extra seats are £{(SEAT_PRICE_PENCE / 100).toFixed(0)}/month each, billed monthly via
          Stripe.
        </Typography>

        {/* Stripe-not-ready guard */}
        {!chargesEnabled && (
          <Alert
            severity="warning"
            icon={<Warning />}
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => navigate(`/store/${storeId}/payments`)}
              >
                Set up
              </Button>
            }
          >
            Connect Stripe payouts before purchasing seats.
          </Alert>
        )}

        {/* Card setup step — shown instead of the normal counter when collecting card */}
        {cardSetup ? (
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Elements stripe={stripePromise} options={{ locale: 'en-GB' }}>
                <CardSetupForm
                  clientSecret={cardSetup.clientSecret}
                  onSuccess={handleCardSuccess}
                  onCancel={() => setCardSetup(null)}
                  saving={saving}
                />
              </Elements>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Current usage card */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Currently
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {activeSeatsUsed} of {seatCount} seats used
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {paidNow > 0
                        ? `${paidNow} paid seat${paidNow === 1 ? '' : 's'} · ${formatGBP(paidNow * SEAT_PRICE_PENCE)}/month`
                        : 'You\'re on the free tier.'}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<ConfirmationNumber />}
                    label={`${seatCount} total`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              </CardContent>
            </Card>

            {/* Saved card indicator */}
            {hasSeatSubscription && (
              <Alert severity="success" icon={<CreditCard />} sx={{ mb: 3, borderRadius: 2 }}>
                You have a saved payment card on file for seat billing.
              </Alert>
            )}

            {/* Counter card */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="overline" color="text.secondary">
                  Change to
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={3} sx={{ my: 2 }}>
                  <IconButton
                    onClick={dec}
                    disabled={draft <= minSeats}
                    aria-label="Reduce seats"
                    size="large"
                    sx={{ border: 1, borderColor: 'divider' }}
                  >
                    <Remove />
                  </IconButton>
                  <Box sx={{ minWidth: 96, textAlign: 'center' }}>
                    <Typography variant="h2" fontWeight={700} lineHeight={1}>
                      {draft}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      seats
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={inc}
                    disabled={draft >= MAX_SEATS}
                    aria-label="Add seats"
                    size="large"
                    sx={{ border: 1, borderColor: 'divider' }}
                  >
                    <Add />
                  </IconButton>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Cost preview */}
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body2" color="text.secondary">
                    {paidDraft} paid seat{paidDraft === 1 ? '' : 's'} after change
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatGBP(paidDraft * SEAT_PRICE_PENCE)}/month total
                  </Typography>
                </Stack>
                {hasChange && (
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {monthlyDiffPence > 0
                        ? 'Extra (next invoice)'
                        : 'You save (next invoice)'}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color={monthlyDiffPence > 0 ? 'warning.main' : 'success.main'}
                    >
                      {monthlyDiffPence > 0 ? '+' : '−'}
                      {formatGBP(Math.abs(monthlyDiffPence))}/month
                    </Typography>
                  </Stack>
                )}

                {draft < activeSeatsUsed && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    You have {activeSeatsUsed} active barbers. Deactivate or remove
                    staff before reducing below that count.
                  </Alert>
                )}

                {needsCard && hasChange && (
                  <Alert severity="info" icon={<CreditCard />} sx={{ mt: 2 }}>
                    A card is required for your first paid seat purchase.
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Action */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
              <Button
                variant="text"
                onClick={() => navigate(`/store/${storeId}/staff`)}
                sx={{ fontWeight: 600 }}
              >
                Back to staff
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!hasChange || saving || creatingIntent || !chargesEnabled || draft < minSeats}
                startIcon={(saving || creatingIntent) ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ borderRadius: 2, px: 3, py: 1.25, fontWeight: 600, minWidth: 160 }}
              >
                {saving || creatingIntent
                  ? 'Processing…'
                  : needsCard && hasChange
                    ? 'Add card & save'
                    : hasSeatSubscription && hasChange
                      ? 'Review changes'
                      : hasChange
                        ? 'Save changes'
                        : 'No changes'}
              </Button>
            </Stack>

            {/* Footnote */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 3, textAlign: 'center' }}
            >
              Stripe pro-rates mid-cycle changes. The adjustment will appear on
              your next invoice.
              {' '}
              <MuiLink
                href="https://stripe.com/docs/billing/subscriptions/prorations"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                Learn more
              </MuiLink>
            </Typography>
          </>
        )}
      </motion.div>

      <CustomSnackbar
        snackbar={{
          open: snackbarState.open,
          message: snackbarState.message,
          severity: snackbarState.severity,
          title: snackbarState.title,
          autoHideDuration: snackbarState.autoHideDuration,
        }}
        onClose={closeSnackbar}
      />

      {/* Confirmation dialog for changes to an existing subscription */}
      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm seat change</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              You are changing from <strong>{seatCount}</strong> to{' '}
              <strong>{pendingCountRef.current}</strong> seats.
            </Typography>
            <Typography variant="body2">
              New monthly cost:{' '}
              <strong>
                {formatGBP(Math.max(0, pendingCountRef.current - FREE_SEATS) * SEAT_PRICE_PENCE)}
                /month
              </strong>
            </Typography>
            {(() => {
              const confirmedPaid = Math.max(0, pendingCountRef.current - FREE_SEATS);
              const diffPence = (confirmedPaid - paidNow) * SEAT_PRICE_PENCE;
              return diffPence !== 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {diffPence > 0
                    ? `+${formatGBP(diffPence)}/month will appear as a proration on your next invoice.`
                    : `${formatGBP(Math.abs(diffPence))}/month will be credited on your next invoice.`}
                </Typography>
              ) : null;
            })()}
            <Alert severity="info" icon={<CreditCard />} sx={{ mt: 1 }}>
              Your saved card on file will be used for billing.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowConfirm(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => doUpdateSeats()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ fontWeight: 600 }}
          >
            {saving ? 'Processing…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageSeats;
